import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  PageHeader,
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Spinner,
  HtmlEditor,
  useToast,
} from '@orientamos/ui'
import { Save, Eye, Code, ChevronLeft } from 'lucide-react'
import type { Editor } from '@tiptap/react'
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  type TemplateField,
  type TemplateExtractionResult,
} from '@lib/api'

const typeOptions = [
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
]

// Sample data for preview (Spanish placeholder keys)
const SAMPLE_DATA: Record<string, string | number> = {
  'propietario.nombre': 'Juan Carlos',
  'propietario.apellido': 'Rodriguez Martinez',
  'propietario.cedula': '1.234.567.890',
  'propietario.ciudad': 'Medellin',
  'propietario.direccion': 'Carrera 80 # 10-20',
  'propietario.correo': 'jcrodriguez@correo.com',
  'arrendatario.nombre': 'Maria Fernanda',
  'arrendatario.apellido': 'Lopez Garcia',
  'arrendatario.cedula': '9.876.543.210',
  'arrendatario.ciudad': 'Medellin',
  'arrendatario.direccion': 'Calle 50 # 30-40',
  'arrendatario.correo': 'mflopez@correo.com',
  'deudorSolidario.nombre': 'Pedro Antonio',
  'deudorSolidario.apellido': 'Gomez Ruiz',
  'deudorSolidario.cedula': '5.555.555.555',
  'deudorSolidario.ciudad': 'Medellin',
  'inmueble.direccion': 'Calle 45 # 23-15, Apto 502',
  'inmueble.nombreEdificio': 'Edificio Los Pinos',
  'inmueble.ciudad': 'Medellin',
  'inmueble.estrato': 4,
  'inmueble.area': 85,
  'inmueble.parqueadero': 1,
  'inmueble.deposito': 1,
  'terminos.canonMensual': '2.500.000',
  'terminos.cuotaAdministracion': '350.000',
  'terminos.duracionMeses': 12,
  'terminos.fechaInicio': '1 de Febrero de 2025',
  'terminos.fechaFin': '31 de Enero de 2026',
  'terminos.diasGracia': 5,
  clausulasAdicionales: '',
}

/**
 * Parse HTML to extract {{placeholder}} fields
 */
const fieldLabelTranslations: Record<string, string> = {
  propietario: 'Propietario',
  arrendatario: 'Arrendatario',
  deudorSolidario: 'Deudor Solidario',
  inmueble: 'Inmueble',
  terminos: 'Terminos',
  nombre: 'Nombre',
  apellido: 'Apellido',
  nombreCompleto: 'Nombre completo',
  cedula: 'Cedula',
  expedidaEn: 'Lugar de expedicion',
  correo: 'Correo',
  telefono: 'Telefono',
  direccion: 'Direccion',
  ciudad: 'Ciudad',
  profesion: 'Profesion',
  tipoPersona: 'Tipo de persona',
  razonSocial: 'Razon social',
  nit: 'NIT',
  nombreRepresentante: 'Representante legal',
  cedulaRepresentante: 'Cedula representante',
  tipoInmueble: 'Tipo de inmueble',
  estrato: 'Estrato',
  area: 'Area',
  nombreEdificio: 'Edificio/Conjunto',
  parqueadero: 'Parqueadero',
  deposito: 'Deposito',
  canonMensual: 'Canon mensual',
  cuotaAdministracion: 'Administracion',
  fechaInicio: 'Fecha inicio',
  fechaFin: 'Fecha fin',
  duracionMeses: 'Duracion (meses)',
  formulaIncremento: 'Formula de incremento',
  diasGracia: 'Dias de gracia',
  clausulasAdicionales: 'Clausulas adicionales',
}

function parseFields(html: string): TemplateField[] {
  const regex = /\{\{([^}]+)\}\}/g
  const matches = html.matchAll(regex)
  const fields: TemplateField[] = []
  const seenKeys = new Set<string>()

  for (const match of matches) {
    const placeholder = match[1]?.trim()
    if (!placeholder) continue
    if (seenKeys.has(placeholder)) continue
    seenKeys.add(placeholder)

    const parts = placeholder.split('.')
    let label = placeholder
    let source: string | undefined
    let sourcePath: string | undefined

    if (parts.length >= 2) {
      source = parts[0]
      sourcePath = parts.slice(1).join('.')
      label = parts
        .map(
          (p) =>
            fieldLabelTranslations[p] ||
            p
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())
              .trim()
        )
        .join(' - ')
    } else {
      label =
        fieldLabelTranslations[placeholder] ||
        placeholder
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim()
    }

    fields.push({
      key: placeholder,
      label,
      type: 'text',
      required: true,
      source,
      sourcePath,
    })
  }

  return fields
}

/**
 * Replace placeholders with sample data
 */
function renderPreview(html: string, data: Record<string, string | number>): string {
  return html.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const value = data[key.trim()]
    if (value !== undefined) {
      return `<span class="preview-value" style="background-color: #fef3c7; padding: 0 2px;">${value}</span>`
    }
    return `<span class="preview-placeholder" style="background-color: #fee2e2; padding: 0 2px;">${match}</span>`
  })
}

interface AIImportState {
  aiImport?: TemplateExtractionResult
}

export default function TemplateEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { addToast } = useToast()
  const isNew = !id || id === 'new'

  // Check for AI-imported data in location state
  const aiImport = (location.state as AIImportState | null)?.aiImport

  const [isLoading, setIsLoading] = useState(!isNew)
  const [isSaving, setIsSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(true)
  const editorRef = useRef<Editor | null>(null)

  // Form state — initialize from AI import if available
  const [name, setName] = useState(aiImport?.name || '')
  const [type, setType] = useState<'residential' | 'commercial'>(
    aiImport?.contractType || 'residential'
  )
  const [region, setRegion] = useState('Medellin')
  const [htmlContent, setHtmlContent] = useState(
    aiImport?.htmlContent ||
      `<div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12pt; line-height: 1.8; color: #000;">

  <p style="text-align: center; font-weight: bold; font-size: 14pt; margin: 0 0 8px; text-transform: uppercase;">
    CONTRATO DE ARRENDAMIENTO
  </p>

  <p style="text-align: center; font-weight: bold; font-size: 11pt; margin: 0 0 24px;">
    LUGAR Y FECHA DEL CONTRATO: MEDELLIN, {{terminos.fechaInicio}}
  </p>

  <table style="width: 100%; margin-bottom: 24px; font-size: 11pt; border-collapse: collapse;">
    <tr>
      <td style="padding: 4px 8px; font-weight: bold; width: 40%;">DIRECCION DEL INMUEBLE:</td>
      <td style="padding: 4px 8px;">{{inmueble.direccion}}, {{inmueble.ciudad}}</td>
    </tr>
    <tr>
      <td style="padding: 4px 8px; font-weight: bold;">ARRENDADOR:</td>
      <td style="padding: 4px 8px;">{{propietario.nombre}} {{propietario.apellido}} - C.C. {{propietario.cedula}}</td>
    </tr>
    <tr>
      <td style="padding: 4px 8px; font-weight: bold;">ARRENDATARIO:</td>
      <td style="padding: 4px 8px;">{{arrendatario.nombre}} {{arrendatario.apellido}} - C.C. {{arrendatario.cedula}}</td>
    </tr>
  </table>

  <p style="text-align: justify; margin: 0 0 12px;">
    Entre los suscritos, <strong>{{propietario.nombre}} {{propietario.apellido}}</strong>, identificado con cedula No. <strong>{{propietario.cedula}}</strong>, quien se denominara EL ARRENDADOR; y <strong>{{arrendatario.nombre}} {{arrendatario.apellido}}</strong>, identificado con cedula No. <strong>{{arrendatario.cedula}}</strong>, quien se denominara EL ARRENDATARIO.
  </p>

  <p style="text-align: justify; margin: 0 0 12px;">
    <strong>PRIMERA: OBJETO DEL CONTRATO:</strong> El inmueble ubicado en <strong>{{inmueble.direccion}}</strong>, de la ciudad de <strong>{{inmueble.ciudad}}</strong>.
  </p>

  <p style="text-align: justify; margin: 0 0 12px;">
    <strong>SEGUNDA: CANON DE ARRENDAMIENTO:</strong> El canon mensual sera de <strong>{{terminos.canonMensual}}</strong> PESOS M/CTE.
  </p>

  {{clausulasAdicionales}}

  <div style="display: flex; justify-content: space-between; margin-top: 48px;">
    <div style="width: 45%; text-align: center;">
      <div style="border-top: 1px solid #000; padding-top: 8px; margin-top: 48px;">
        <p style="margin: 4px 0; font-weight: bold;">EL ARRENDADOR</p>
        <p style="margin: 4px 0;">{{propietario.nombre}} {{propietario.apellido}}</p>
        <p style="margin: 4px 0;">C.C. {{propietario.cedula}}</p>
      </div>
    </div>
    <div style="width: 45%; text-align: center;">
      <div style="border-top: 1px solid #000; padding-top: 8px; margin-top: 48px;">
        <p style="margin: 4px 0; font-weight: bold;">EL ARRENDATARIO</p>
        <p style="margin: 4px 0;">{{arrendatario.nombre}} {{arrendatario.apellido}}</p>
        <p style="margin: 4px 0;">C.C. {{arrendatario.cedula}}</p>
      </div>
    </div>
  </div>
</div>`
  )

  // Parsed fields
  const parsedFields = useMemo(() => parseFields(htmlContent), [htmlContent])

  // Preview HTML
  const previewHtml = useMemo(() => renderPreview(htmlContent, SAMPLE_DATA), [htmlContent])

  useEffect(() => {
    if (!isNew && id) {
      loadTemplate(id)
    }
  }, [id, isNew])

  const loadTemplate = async (templateId: string) => {
    setIsLoading(true)
    try {
      const result = await getTemplate(templateId)
      if (result.success && result.data) {
        setName(result.data.name)
        setType(result.data.type as 'residential' | 'commercial')
        setRegion(result.data.region)
        setHtmlContent(result.data.htmlContent)
      } else {
        addToast({
          variant: 'error',
          message: 'No se pudo cargar la plantilla.',
        })
        navigate('/templates')
      }
    } catch (err) {
      console.error('Error loading template:', err)
      addToast({
        variant: 'error',
        message: 'Error de conexion.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      addToast({
        variant: 'error',
        message: 'El nombre de la plantilla es requerido.',
      })
      return
    }

    if (!htmlContent.trim()) {
      addToast({
        variant: 'error',
        message: 'El contenido HTML es requerido.',
      })
      return
    }

    setIsSaving(true)
    try {
      if (isNew) {
        const result = await createTemplate({
          name,
          type,
          region,
          htmlContent,
          fields: parsedFields,
        })
        if (result.success && result.data) {
          addToast({
            variant: 'success',
            message: 'La plantilla ha sido creada exitosamente.',
          })
          navigate(`/templates/${result.data.id}/edit`)
        } else {
          throw new Error(result.error)
        }
      } else {
        const result = await updateTemplate(id!, {
          name,
          type,
          region,
          htmlContent,
          fields: parsedFields,
        })
        if (result.success) {
          addToast({
            variant: 'success',
            message: 'Plantilla actualizada. Los cambios han sido guardados.',
          })
        } else {
          throw new Error(result.error)
        }
      }
    } catch (err) {
      console.error('Error saving template:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo guardar la plantilla.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const insertField = (field: TemplateField) => {
    const placeholder = `{{${field.key}}}`
    if (editorRef.current) {
      editorRef.current.chain().focus().insertContent(placeholder).run()
    } else {
      setHtmlContent((prev) => prev + placeholder)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title={isNew ? 'Nueva Plantilla' : 'Editar Plantilla'}
        description={isNew ? 'Crea una nueva plantilla de contrato' : name}
        breadcrumbs={[
          { label: 'Inicio', href: '/' },
          { label: 'Plantillas', href: '/templates' },
          { label: isNew ? 'Nueva' : 'Editar' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/templates')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? (
                <>
                  <Code className="w-4 h-4 mr-1" />
                  Editor
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Vista Previa
                </>
              )}
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              <Save className="w-4 h-4 mr-1" />
              Guardar
            </Button>
          </div>
        }
      />

      <div className="flex-1 grid grid-cols-12 gap-6 min-h-0">
        {/* Left side: Editor or Preview */}
        <div className="col-span-12 lg:col-span-9 flex flex-col min-h-0">
          {/* Form fields */}
          <Card className="mb-4" padding="sm">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Input
                  label="Nombre de la Plantilla"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Contrato de Arrendamiento Residencial"
                />
              </div>
              <div>
                <Select
                  label="Tipo de Contrato"
                  options={typeOptions}
                  value={type}
                  onChange={(e) => setType(e.target.value as 'residential' | 'commercial')}
                />
              </div>
              <div>
                <Input
                  label="Region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder="Ej: Medellin"
                />
              </div>
            </div>
          </Card>

          {/* Editor/Preview area */}
          <Card className="flex-1 min-h-0 overflow-hidden flex flex-col" padding="none">
            {showPreview ? (
              <div className="flex-1 overflow-auto p-4">
                <div className="bg-white border border-neutral-200 rounded shadow-sm">
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full min-h-[600px] border-0"
                    title="Vista previa"
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-sm text-neutral-600">
                  Usa <code className="bg-neutral-200 px-1 rounded">{'{{campo}}'}</code> para campos
                  dinamicos
                </div>
                <HtmlEditor
                  value={htmlContent}
                  onChange={setHtmlContent}
                  editorRef={editorRef}
                  className="flex-1 border-0 rounded-none"
                  placeholder="Escribe el contenido aqui..."
                />
              </div>
            )}
          </Card>
        </div>

        {/* Right sidebar: Available fields */}
        <div className="col-span-12 lg:col-span-3">
          <Card className="sticky top-4">
            <CardHeader title="Campos Disponibles" description="Haz clic para insertar" />
            <div className="mt-4 space-y-4">
              {/* Grouped by source */}
              {['propietario', 'arrendatario', 'deudorSolidario', 'inmueble', 'terminos'].map(
                (source) => {
                  const sourceFields = parsedFields.filter((f) => f.source === source)
                  const sourceLabel = {
                    propietario: 'Propietario',
                    arrendatario: 'Arrendatario',
                    deudorSolidario: 'Deudor Solidario',
                    inmueble: 'Inmueble',
                    terminos: 'Terminos',
                  }[source]

                  if (sourceFields.length === 0) return null

                  return (
                    <div key={source}>
                      <h4 className="text-xs font-semibold text-neutral-500 uppercase mb-2">
                        {sourceLabel}
                      </h4>
                      <div className="space-y-1">
                        {sourceFields.map((field) => (
                          <button
                            key={field.key}
                            onClick={() => insertField(field)}
                            className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-neutral-100 transition-colors"
                          >
                            <code className="text-primary-600">{`{{${field.key}}}`}</code>
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                }
              )}

              {/* Common placeholders */}
              <div>
                <h4 className="text-xs font-semibold text-neutral-500 uppercase mb-2">
                  Campos Comunes
                </h4>
                <div className="space-y-1">
                  {[
                    { key: 'propietario.nombre', label: 'Nombre propietario' },
                    { key: 'propietario.apellido', label: 'Apellido propietario' },
                    { key: 'propietario.cedula', label: 'Cedula propietario' },
                    { key: 'arrendatario.nombre', label: 'Nombre arrendatario' },
                    { key: 'arrendatario.apellido', label: 'Apellido arrendatario' },
                    { key: 'arrendatario.cedula', label: 'Cedula arrendatario' },
                    { key: 'inmueble.direccion', label: 'Direccion inmueble' },
                    { key: 'inmueble.ciudad', label: 'Ciudad' },
                    { key: 'terminos.canonMensual', label: 'Canon mensual' },
                    { key: 'terminos.fechaInicio', label: 'Fecha inicio' },
                    { key: 'terminos.fechaFin', label: 'Fecha fin' },
                    { key: 'clausulasAdicionales', label: 'Clausulas adicionales' },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() =>
                        insertField({
                          key: item.key,
                          label: item.label,
                          type: 'text',
                          required: true,
                        })
                      }
                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-neutral-100 transition-colors flex items-center justify-between"
                    >
                      <span className="text-neutral-600">{item.label}</span>
                      <code className="text-xs text-primary-600">{`{{${item.key}}}`}</code>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
